import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, CreditCard, Loader2, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';

export default function Billing({ api }) {
  const [subscriptionState, setSubscriptionState] = useState(null);
  const [plansState, setPlansState] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const plans = plansState?.plans || [];
  const subscription = subscriptionState?.subscription;
  const activeUsersCount = plansState?.active_users_count || subscriptionState?.active_users_count || 1;
  const displayPlan = subscription?.plan_key || (subscriptionState?.is_trial ? 'Trial' : '-');
  const displayCycle = subscriptionState?.is_trial
    ? 'Trial'
    : subscription?.billing_cycle === 'annual'
      ? 'Anual'
      : subscription?.billing_cycle === 'monthly'
        ? 'Mensal'
        : '-';
  const displayLicenses = subscription?.billable_licenses
    ? `${subscription.billable_licenses} contratadas / ${activeUsersCount} em uso`
    : `${activeUsersCount} em uso`;

  const loadBilling = useCallback(async () => {
    setLoading(true);
    try {
      const [subscriptionRes, plansRes] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/plans'),
      ]);
      setSubscriptionState(subscriptionRes.data);
      setPlansState(plansRes.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível carregar o faturamento.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const formatCents = (cents) => {
    if (cents == null) return 'Sob consulta';
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('pt-BR');
  };

  const formatDaysRemaining = (days) => {
    if (days == null) return null;
    if (days === 0) return 'vence hoje';
    if (days === 1) return 'resta 1 dia';
    return `restam ${days} dias`;
  };

  const formatDaysOverdue = (days) => {
    if (days == null || days <= 0) return null;
    if (days === 1) return 'vencido há 1 dia';
    return `vencido há ${days} dias`;
  };

  const periodEnd = subscription?.current_period_end || subscriptionState?.current_period_end;
  const daysRemainingText = subscriptionState?.is_blocked ? null : formatDaysRemaining(subscriptionState?.days_remaining);
  const daysOverdueText = subscriptionState?.is_blocked ? formatDaysOverdue(subscriptionState?.days_overdue) : null;

  const currentBadge = useMemo(() => {
    if (subscriptionState?.is_blocked) {
      return { label: 'Bloqueada', className: 'bg-red-100 text-red-700 hover:bg-red-100' };
    }
    if (subscriptionState?.is_trial) {
      return { label: 'Trial ativo', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' };
    }
    if (!subscriptionState?.has_subscription) {
      return { label: 'Sem assinatura', className: 'bg-slate-100 text-slate-700 hover:bg-slate-100' };
    }
    if (subscriptionState.is_active) {
      return { label: subscription?.status === 'trialing' ? 'Trial ativo' : 'Ativa', className: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' };
    }
    if (subscriptionState.is_pending) {
      return { label: 'Pagamento pendente', className: 'bg-amber-100 text-amber-700 hover:bg-amber-100' };
    }
    return { label: 'Bloqueada', className: 'bg-red-100 text-red-700 hover:bg-red-100' };
  }, [subscriptionState, subscription]);

  const createCheckout = async (plan) => {
    setActionLoading(`${plan.plan_key}:checkout`);
    try {
      const res = await api.post('/billing/checkout-session', {
        plan_key: plan.plan_key,
        billing_cycle: billingCycle,
      });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível iniciar o checkout.');
    } finally {
      setActionLoading(null);
    }
  };

  const openPortal = async () => {
    setActionLoading('portal');
    try {
      const res = await api.post('/billing/portal-session', { return_url: window.location.href });
      window.location.href = res.data.portal_url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível abrir o portal de faturamento.');
    } finally {
      setActionLoading(null);
    }
  };

  const syncPlans = async () => {
    setActionLoading('sync');
    try {
      await api.post('/billing/plans/sync');
      await loadBilling();
      toast.success('Planos sincronizados com o Stripe.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível sincronizar os planos.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Faturamento</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie a licença do sistema, pagamentos e usuários contratados.</p>
        </div>
        <Button variant="outline" onClick={syncPlans} disabled={actionLoading === 'sync'}>
          {actionLoading === 'sync' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sincronizar Stripe
        </Button>
      </div>

      <Card className={subscriptionState?.is_blocked ? 'border-red-200' : subscriptionState?.is_pending ? 'border-amber-200' : 'border-emerald-200'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-lg">
              {subscriptionState?.is_blocked ? (
                <TriangleAlert className="w-5 h-5 text-red-600" />
              ) : (
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              )}
              Licença do sistema
            </CardTitle>
            <Badge className={currentBadge.className}>{currentBadge.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Plano</p>
              <p className="font-semibold text-slate-900">{displayPlan}</p>
            </div>
            <div>
              <p className="text-slate-500">Ciclo</p>
              <p className="font-semibold text-slate-900">{displayCycle}</p>
            </div>
            <div>
              <p className="text-slate-500">Licenças</p>
              <p className="font-semibold text-slate-900">{displayLicenses}</p>
            </div>
            <div>
              <p className="text-slate-500">{subscriptionState?.is_trial ? 'Fim do trial' : 'Vencimento'}</p>
              <p className="font-semibold text-slate-900">{formatDate(periodEnd)}</p>
              {daysRemainingText && <p className="text-xs text-slate-500">{daysRemainingText}</p>}
              {daysOverdueText && <p className="text-xs text-red-600">{daysOverdueText}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={openPortal} disabled={!subscriptionState?.has_subscription || actionLoading === 'portal'}>
              {actionLoading === 'portal' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Abrir portal Stripe
            </Button>
            {subscriptionState?.is_pending && (
              <p className="text-sm text-amber-700">Há uma pendência de pagamento nesta licença.</p>
            )}
            {subscriptionState?.is_trial && daysRemainingText && (
              <p className="text-sm text-emerald-700">Trial ativo: {daysRemainingText}.</p>
            )}
            {subscriptionState?.is_blocked && (
              <p className="text-sm text-red-600">
                A licença está vencida{daysOverdueText ? ` (${daysOverdueText})` : ''}. Regularize o pagamento para liberar o sistema.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Planos disponíveis</h2>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <button
            type="button"
            className={`px-3 py-1.5 text-sm rounded-md ${billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Mensal
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-sm rounded-md ${billingCycle === 'annual' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
            onClick={() => setBillingCycle('annual')}
          >
            Anual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const price = plan[billingCycle];
          const total = price?.unit_amount_cents || 0;
          const isCurrent = subscription?.plan_key === plan.plan_key && subscription?.billing_cycle === billingCycle;
          const cannotContract = !plan.variable_licenses && activeUsersCount > plan.minimum_licenses;
          return (
            <Card key={plan.plan_key} className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{plan.name}</CardTitle>
                  {isCurrent && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Atual</Badge>}
                </div>
                <Badge className="w-fit mt-1 bg-slate-100 text-slate-600 hover:bg-slate-100">
                  {plan.variable_licenses ? `a partir de ${plan.minimum_licenses} licenças` : `inclui ${plan.minimum_licenses} licenças`}
                </Badge>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 justify-between gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-[11px] uppercase text-slate-400 font-medium">Valor do plano</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCents(price?.unit_amount_cents)}
                      <span className="text-sm font-normal text-slate-500">/{billingCycle === 'annual' ? 'ano' : 'mês'}</span>
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase text-slate-400 font-medium">Total</p>
                    <p className="font-semibold text-slate-900">
                      {formatCents(total)}
                      <span className="text-xs font-normal text-slate-500">/{billingCycle === 'annual' ? 'ano' : 'mês'}</span>
                    </p>
                    {plan.variable_licenses && (
                      <p className="text-xs text-slate-500">
                        Calculado para {plan.billable_licenses} licenças.
                      </p>
                    )}
                  </div>
                  {billingCycle === 'annual' && plan.annual_savings_cents > 0 && (
                    <p className="text-sm text-emerald-700 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" />
                      Economia anual mínima de {formatCents(plan.annual_savings_cents)}
                    </p>
                  )}
                  {cannotContract && (
                    <p className="text-sm text-red-600">
                      Este plano possui menos licenças que os {activeUsersCount} usuários ativos.
                    </p>
                  )}
                </div>

                <Button
                  className="w-full"
                  variant={isCurrent ? 'outline' : 'default'}
                  disabled={cannotContract || actionLoading === `${plan.plan_key}:checkout`}
                  onClick={() => createCheckout(plan)}
                >
                  {actionLoading === `${plan.plan_key}:checkout` ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Contratar no Stripe'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
